import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../../../shared/services/api.service';
import * as AppSelectors from '../../../store/app/app.selectors';
import { PasswordValidator } from '../../../validators/password-validator';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app/app.state';
import { LATE_ERROR_STATE } from '../../../shared/helpers/error-state';
import { sha256Hex } from '../../../shared/helpers/sha256';

@Component({
    selector: 'urban-recovery',
    templateUrl: './recovery.component.html',
    styleUrls: ['./recovery.component.scss'],
    standalone: false,
    providers: [LATE_ERROR_STATE]
})
export class RecoveryComponent implements OnInit {
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.setLittleScreen();
  }

  private readonly fb = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly appStore = inject(Store<AppState>);  
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroytRef = inject(DestroyRef);

  public error: string | null = null;
  public success: string | null;
  public hideNewPassword: boolean = true;
  public hideConfirmedNewPassword: boolean = true;
  public token: string | null;
  public isLittleScreen: boolean;
  public form: UntypedFormGroup;
  private readonly regexPassword: string = "^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$";
  public icons: Record<string, SafeHtml> = {};

  ngOnInit(): void {
    this.setLittleScreen();

    this.form = this.fb.group(
      {
        newPassword: ['', Validators.compose([Validators.required, Validators.minLength(5), Validators.pattern(this.regexPassword)])],
        confirmedNewPassword: ['', Validators.compose([Validators.required, Validators.minLength(5), Validators.pattern(this.regexPassword)])]
      }, 
      { 
        validators: PasswordValidator.MatchPassword 
      }
    );

    this.route.paramMap.pipe(
      map((res: any) => res.params['token']),
      tap((token: string) => this.token = token),
      switchMap((token: string) => this.apiService.checkToken(token)),
      takeUntilDestroyed(this.destroytRef)
    ).subscribe({
        next: () => {},
        error: () => this.router.navigate(['forgot-password'])
      });
    
    this.appStore.select(AppSelectors.getIcons).pipe(first()).subscribe(response => {
      response.filter(icon => ['visibility-outline', 'visibility-off-outline'].includes(icon.Name))
        .forEach(icon => {
          this.icons[icon.Name] = this.sanitizer.bypassSecurityTrustHtml(icon.Content);
        });
      });
    }

  private setLittleScreen(): void { 
    if (window.innerWidth > 800) {
      this.isLittleScreen = false;
    } else {
      this.isLittleScreen = true;
    }
  }

  public async submit(): Promise<void> {
    if (this.form.valid) {
      let newPassword = await sha256Hex(this.form.get('newPassword').value);
      this.apiService.resetPassword(newPassword, this.token)
        .pipe(takeUntilDestroyed(this.destroytRef))
        .subscribe(
          (res: any) => {
            if (res.Result === "fail") {
              this.success = null;
              this.error = 'ERROR_FAIL';
            } else {
              this.error = null;
              this.success = 'SUCCESS';

              setTimeout(() => {
                this.router.navigate(['login'])
              }, 1500);
            }
          },
        );
    }

    else {
      if (this.form.controls.newPassword.errors?.required == true || this.form.controls.confirmedNewPassword.errors?.required == true) {
        this.translate.get('CHANGE_PASSWORD_DIALOG.ERROR_EMPTY').pipe(takeUntilDestroyed(this.destroytRef)).subscribe(res => {
          this.error = res;
        });
      }
      else {
        if (this.form.controls.newPassword.errors?.minlength) {
          this.translate.get('CREATE_DASHBOARD_DIALOG.ERROR').pipe(takeUntilDestroyed(this.destroytRef)).subscribe(res => {
            this.error = res;
          });
        }
        else {
          if (this.form.controls.confirmedNewPassword.errors?.MatchPassword) {
            this.translate.get('CHANGE_PASSWORD_DIALOG.ERROR_EQUAL').pipe(takeUntilDestroyed(this.destroytRef)).subscribe(res => {
              this.error = res;
            });
          }
          else if (this.form.controls.newPassword.errors?.pattern) {
            this.translate.get('CHANGE_PASSWORD_DIALOG.ERROR_SYNTAX').pipe(takeUntilDestroyed(this.destroytRef)).subscribe(res => {
              this.error = res;
            });
          }
        }
      }
    }
  }

  public get passwordStrength(): number {
    const value: string = this.form?.get('newPassword')?.value ?? '';
    if (!value) {
      return 0;
    }

    let strength = 0;
    if (value.length >= 8) strength++;
    if (/\d/.test(value)) strength++;
    if (/[a-z]/.test(value) && /[A-Z]/.test(value)) strength++;
    if (value.length >= 12 || /[^A-Za-z0-9]/.test(value)) strength++;

    return strength;
  }

  public changeVisibility(field: 'new' | 'confirmed'): void {
    switch (field) {
      case 'new':
        this.hideNewPassword = !this.hideNewPassword
        break;
      case 'confirmed':
        this.hideConfirmedNewPassword = !this.hideConfirmedNewPassword
        break;
    }
  }
}
